import { gql } from '@apollo/client';
import type { ProductFilterValue } from '@apiTypes';
import useCancelableQuery from '../../core/hooks/apollo/useCancelableQuery';
import type { ProductListQueryFilters } from '../components/ProductList/context/ProductListOptionsContext';
import { useProductListOptionsContext } from '../components/ProductList/context/ProductListOptionsContext';

export const productFilterQuery = gql`
    query productFilterValues(
        $filterField: String!
        $store: ID!
        $language: Language!
        $ids: [ID!]
        $categoryId: ID
        $filters: [ProductListFilter]
        $minPrice: Float
        $maxPrice: Float
        $search: String
        $vehicleId: String
        $bicycleId: ID
        $onSale: Boolean
        $isNew: Boolean
        $states: [ProductState]
        $preorderSeasonId: Int
        $includePreorderSeasonId: Int
        $bestAvailability: ProductBestAvailabilityFilter
        $includeExcludedCategories: Boolean
        $attributeFilters: JSON
    ) {
        productFilterValues(
            filterField: $filterField
            store: $store
            language: $language
            ids: $ids
            categoryId: $categoryId
            filters: $filters
            minPrice: $minPrice
            maxPrice: $maxPrice
            search: $search
            vehicleId: $vehicleId
            sparePartParentId: $bicycleId
            onSale: $onSale
            isNew: $isNew
            states: $states
            preorderSeasonId: $preorderSeasonId
            includePreorderSeasonId: $includePreorderSeasonId
            bestAvailability: $bestAvailability
            includeExcludedCategories: $includeExcludedCategories
            attributeFilters: $attributeFilters
        ) {
            value
            valueLabel
            total
        }
    }
`;

const useProductFilterCount = ({
    filterField,
    filterExceptions,
    additionalFilters,
    skip,
}: {
    filterField: string;
    filterExceptions?: string[];
    additionalFilters?: Partial<ProductListQueryFilters>;
    skip?: boolean;
}) => {
    const filterExceptionsSet = new Set(filterExceptions);
    const {
        filters: {
            store,
            language,
            minPrice,
            maxPrice,
            vehicleId,
            bicycleId,
            search,
            filters,
            states,
            categoryId,
            bestAvailability,
            ...others
        },
        isFiltersLoading,
    } = useProductListOptionsContext();

    const {
        data: { productFilterValues } = { productFilterValues: [] },
        loading,
        error,
    } = useCancelableQuery<{
        productFilterValues: ProductFilterValue[];
    }>(
        productFilterQuery,
        {
            variables: {
                filterField,
                store,
                language,
                categoryId,
                filters: filters.filter(
                    ({ name }) =>
                        name !== filterField && !filterExceptionsSet.has(name)
                ),
                minPrice,
                maxPrice,
                search,
                vehicleId,
                bicycleId,
                states,
                bestAvailability,
                ...others,
                ...additionalFilters,
            },
            skip: isFiltersLoading || skip,
        },
        true
    );

    if (error) {
        throw error;
    }

    return {
        productFilterValues,
        loading: loading,
    };
};

export default useProductFilterCount;
